import type { ApprovedFormulaImplementationDigestInput } from './approved-formulas';

/**
 * Seal a reconciliation VALUE — not a function — into the definition digest.
 *
 * Function-source inputs capture a constant as an identifier only, allowing
 * its behavior to change without invalidating an active formula. A value seal
 * instead serializes the value itself, canonically and deterministically.
 *
 * Explicit type tags distinguish a string such as `0.01` from the number
 * `0.01`; object keys are stable; and set members are sorted by serialized
 * form. The scheme tag is part of the serialized body, so changing this
 * serialization forces a visible digest migration.
 *
 * Sealed entries use `value:<CONSTANT_NAME>` so guard checks can distinguish
 * sealed data from implementation-source inputs.
 */
const VALUE_SEAL_SCHEME = 'formula-value-seal.v1';
const VALUE_SEAL_NAME_PREFIX = 'value:';

function canonicalValueSealNode(value: unknown): unknown {
  if (value === null) return ['null'];
  if (typeof value === 'string') {
    if (/ {2,}/.test(value) || /[^\S ]/.test(value)) {
      throw new Error('value seal: string contains whitespace the digest seam would collapse');
    }
    return ['string', value];
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('value seal: number must be finite');
    return ['number', String(value)];
  }
  if (typeof value === 'boolean') return ['boolean', value];
  if (value instanceof Set) {
    const members = [...value].map((member) => canonicalValueSealNode(member));
    members.sort((left, right) => (JSON.stringify(left) < JSON.stringify(right) ? -1 : 1));
    return ['set', members];
  }
  if (Array.isArray(value)) return ['array', value.map((item) => canonicalValueSealNode(item))];
  if (typeof value === 'object') {
    return [
      'object',
      Object.keys(value)
        .sort()
        .map((key) => [key, canonicalValueSealNode((value as Record<string, unknown>)[key])]),
    ];
  }
  throw new Error(`value seal: ${typeof value} cannot be canonically serialized`);
}

export function valueDigestInput(
  name: string,
  value: unknown
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({
    name: `${VALUE_SEAL_NAME_PREFIX}${name}`,
    source: JSON.stringify([VALUE_SEAL_SCHEME, canonicalValueSealNode(value)]),
  });
}
